
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from 'sonner';

interface Guest {
  name: string;
  email: string;
  phone: string;
}

interface GuestProps {
  guest: Guest;
  onRemove: () => void;
}

const GuestComponent: React.FC<GuestProps> = ({ guest, onRemove }) => {
  return (
    <Badge variant="secondary" className="py-1 px-2 flex items-center gap-2 mb-1">
      <User className="h-3 w-3" />
      <span className="text-xs">{guest.email}</span>
      <X 
        className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" 
        onClick={onRemove} 
      />
    </Badge>
  );
};

interface GuestManagementProps {
  guests: Guest[];
  setGuests: (guests: Guest[]) => void;
}

const GuestManagement: React.FC<GuestManagementProps> = ({ 
  guests, 
  setGuests 
}) => {
  const [newGuestEmail, setNewGuestEmail] = useState("");
  
  const handleAddGuest = () => {
    if (!newGuestEmail.trim()) {
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newGuestEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    // Check for duplicates
    if (guests.some(guest => guest.email === newGuestEmail)) {
      toast.error("This guest has already been added");
      return;
    }
    
    // Add guest and reset input
    const newGuest: Guest = {
      name: '',
      email: newGuestEmail,
      phone: ''
    };
    setGuests([...guests, newGuest]);
    setNewGuestEmail("");
  };
  
  const handleRemoveGuest = (index: number) => {
    const updatedGuests = [...guests];
    updatedGuests.splice(index, 1);
    setGuests(updatedGuests);
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddGuest();
    }
  };
  
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input 
          value={newGuestEmail} 
          onChange={(e) => setNewGuestEmail(e.target.value)} 
          placeholder="Guest email"
          onKeyPress={handleKeyPress}
        />
        <Button 
          type="button" 
          size="icon" 
          variant="outline" 
          onClick={handleAddGuest}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      
      {guests.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {guests.map((guest, index) => (
            <GuestComponent 
              key={index} 
              guest={guest} 
              onRemove={() => handleRemoveGuest(index)} 
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default GuestManagement;
